"use client";

import {
  Box,
  Flex,
  HStack,
  Progress,
  Text,
  VStack,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import {
  getPasswordStrength,
  PASSWORD_REQUIREMENTS,
} from "../lib/passwordPolicy.ts";
import { useAppTheme } from "../ThemeContext.tsx";

interface PasswordStrengthBarProps {
  password: string;
}

function PasswordRequirementsTooltip({ password }: { password: string }) {
  return (
    <VStack align="stretch" spacing={1.5} py={0.5}>
      {PASSWORD_REQUIREMENTS.map((req) => {
        const met = req.test(password);
        return (
          <HStack key={req.id} spacing={2}>
            <Icon
              as={met ? CheckIcon : CloseIcon}
              boxSize={2.5}
              color={met ? "green.300" : "whiteAlpha.600"}
            />
            <Text
              fontSize="xs"
              color={met ? "green.100" : "whiteAlpha.900"}
              whiteSpace="nowrap"
            >
              {req.label}
            </Text>
          </HStack>
        );
      })}
    </VStack>
  );
}

export default function PasswordStrengthBar({
  password,
}: PasswordStrengthBarProps) {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const strength = getPasswordStrength(password);

  const labelColor = isDark ? "gray.400" : "gray.600";
  const hintColor = isDark ? "gray.500" : "gray.400";
  const progressBg = isDark ? "gray.600" : "gray.100";

  return (
    <Tooltip
      label={<PasswordRequirementsTooltip password={password} />}
      placement="top"
      hasArrow
      openDelay={200}
      bg="gray.700"
      color="white"
      px={3}
      py={2}
      borderRadius="md"
      maxW="none"
    >
      <Box w="100%" cursor="help" mt={2}>
        <Flex justify="space-between" align="center" mb={1}>
          <Text fontSize="xs" color={labelColor} fontWeight="medium">
            Password strength
          </Text>
          {password ? (
            <Text fontSize="xs" color={strength.color} fontWeight="semibold">
              {strength.label}
            </Text>
          ) : (
            <Text fontSize="xs" color={hintColor}>
              Hover for requirements
            </Text>
          )}
        </Flex>
        <Progress
          value={strength.score}
          size="sm"
          borderRadius="full"
          colorScheme={
            strength.score === 100
              ? "green"
              : strength.score >= 80
                ? "teal"
                : strength.score >= 60
                  ? "yellow"
                  : strength.score >= 40
                    ? "orange"
                    : "red"
          }
          bg={progressBg}
        />
      </Box>
    </Tooltip>
  );
}
